import { muiGlyph } from "@/components/ui/mui-glyph";

// MUI's two filled arrow glyphs, as `@mui/icons-material` draws them.
//
// Ported for the same reason `mui-alert-icons.tsx` was: the donor draws these
// and Atlas was drawing lucide's outline chevrons in their place, so one donor
// glyph had two shapes depending on which Atlas component you were looking at.
// The pair is worst inside a single control — the category tree's trigger
// draws `ArrowDropDown` while every branch row below it drew a chevron
// (`CategoriesFieldPaginated.tsx:16-17,164-173`, and #542's parity review).
//
// Paths only, and no opinion about ink, size or placement: `SvgIcon` gives a
// 24px box (`fontSize: medium` is `pxToRem(24)` on `width/height: 1em`) and
// `fill: currentColor` with no stroke (`SvgIcon.js:44-45,56,78`), and
// everything past that belongs to whatever slot the glyph sits in. That is why
// `MuiCaretGlyph` — which has a ruled ink and a rotation — takes the path from
// here rather than the component.

/**
 * `@mui/icons-material/ArrowDropDown.js`, as the running donor renders it.
 *
 * Exported, unlike its sibling below, because `MuiCaretGlyph` draws the same
 * triangle under its own ruled ink and rotation and takes the path from here
 * — one spelling of one glyph. (`@mui/x-date-pickers` ships the same geometry
 * written `m7 10 5 5 5-5z`; this is the spelling in the live `.MuiSelect-icon`.)
 */
export const ARROW_DROP_DOWN_PATH = "M7 10l5 5 5-5z";

/** `@mui/icons-material/ArrowDropUp.js`: the Change Summary's open groups (#1178). */
const ARROW_DROP_UP_PATH = "m7 14 5-5 5 5z";

/** `@mui/icons-material/ArrowLeft.js`. */
const ARROW_LEFT_PATH = "m14 7-5 5 5 5z";

// The 24px box: `SvgIcon`'s `fontSize: medium` is `pxToRem(24)` against
// `width/height: 1em` (`SvgIcon.js:44-45,78`).
export const MuiArrowDropDownIcon = muiGlyph("mui-arrow-drop-down", ARROW_DROP_DOWN_PATH, "size-6");
export const MuiArrowDropUpIcon = muiGlyph("mui-arrow-drop-up", ARROW_DROP_UP_PATH, "size-6");
export const MuiArrowLeftIcon = muiGlyph("mui-arrow-left", ARROW_LEFT_PATH, "size-6");

// ## The pager's four
//
// The donor's `PagerButton` maps its kinds onto MUI glyphs directly —
// `FirstPage`, `KeyboardArrowLeft`, `KeyboardArrowRight`, `LastPage`
// (`packages/react-ui/src/data-pager/pager-button.tsx:4-7,48-54`). Atlas drew
// lucide's `ChevronFirst` / `ChevronLeft` / `ChevronRight` / `ChevronLast`,
// which are a different shape rather than a different size of the same one
// (#628 B): lucide's chevrons are stroked outlines, MUI's are filled paths,
// and the first/last pair puts its bar on the other side of the arrow.
//
// **18px, and that is the donor's number too.** `BasePagerButton` defaults to
// `size='small'` (`pager-button.tsx:23`) and MUI gives a small `Button`'s icon
// `fontSize: 18` (`Button.js:60`) — so the size Atlas already used was right
// and only the shape was wrong.

/** `@mui/icons-material/FirstPage.js`. */
const FIRST_PAGE_PATH = "M18.41 16.59 13.82 12l4.59-4.59L17 6l-6 6 6 6zM6 6h2v12H6z";
/** `@mui/icons-material/KeyboardArrowLeft.js`. */
const KEYBOARD_ARROW_LEFT_PATH = "M15.41 16.59 10.83 12l4.58-4.59L14 6l-6 6 6 6z";
/** `@mui/icons-material/KeyboardArrowRight.js`. */
const KEYBOARD_ARROW_RIGHT_PATH = "M8.59 16.59 13.17 12 8.59 7.41 10 6l6 6-6 6z";
/** `@mui/icons-material/LastPage.js`. */
const LAST_PAGE_PATH = "M5.59 7.41 10.18 12l-4.59 4.59L7 18l6-6-6-6zM16 6h2v12h-2z";

export const MuiFirstPageIcon = muiGlyph("mui-first-page", FIRST_PAGE_PATH, "size-[18px]");
export const MuiKeyboardArrowLeftIcon = muiGlyph(
  "mui-keyboard-arrow-left",
  KEYBOARD_ARROW_LEFT_PATH,
  "size-[18px]",
);
export const MuiKeyboardArrowRightIcon = muiGlyph(
  "mui-keyboard-arrow-right",
  KEYBOARD_ARROW_RIGHT_PATH,
  "size-[18px]",
);
export const MuiLastPageIcon = muiGlyph("mui-last-page", LAST_PAGE_PATH, "size-[18px]");

// ## The details table's expander
//
// `SpecUsage.tsx` toggles `KeyboardArrowDownIcon` / `KeyboardArrowUpIcon`
// inside an `IconButton size='small'`, at the `SvgIcon` default of 24px —
// small shrinks the button's padding, not its glyph. Atlas drew lucide's
// stroked `ChevronDown` / `ChevronUp` at 20px there.

/** `@mui/icons-material/KeyboardArrowDown.js`. */
const KEYBOARD_ARROW_DOWN_PATH = "M7.41 8.59 12 13.17l4.59-4.58L18 10l-6 6-6-6z";
/** `@mui/icons-material/KeyboardArrowUp.js`. */
const KEYBOARD_ARROW_UP_PATH = "M7.41 15.41 12 10.83l4.59 4.58L18 14l-6-6-6 6z";

export const MuiKeyboardArrowDownIcon = muiGlyph(
  "mui-keyboard-arrow-down",
  KEYBOARD_ARROW_DOWN_PATH,
  "size-6",
);
export const MuiKeyboardArrowUpIcon = muiGlyph(
  "mui-keyboard-arrow-up",
  KEYBOARD_ARROW_UP_PATH,
  "size-6",
);

// ## The date calendar's month arrows
//
// `PickersArrowSwitcher` draws `ArrowLeft` / `ArrowRight` out of
// `@mui/x-date-pickers/icons` (`PickersArrowSwitcher.mjs`), which are **not**
// the `@mui/icons-material` `ArrowLeft` above — that one is a filled triangle,
// these are chevrons. They are the same geometry as `KeyboardArrowLeft/Right`
// with a closing segment the material copies drop, so they keep their own
// spelling, as the pickers ship it (9.4.0). 24px: the arrow `IconButton` is
// medium and the icon takes `fontSize: inherit` from its 1.5rem.

/** `@mui/x-date-pickers/icons` `ArrowLeftIcon`. */
const PICKER_ARROW_LEFT_PATH = "M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z";
/** `@mui/x-date-pickers/icons` `ArrowRightIcon`. */
const PICKER_ARROW_RIGHT_PATH = "M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z";

export const MuiPickerArrowLeftIcon = muiGlyph(
  "mui-picker-arrow-left",
  PICKER_ARROW_LEFT_PATH,
  "size-6",
);
export const MuiPickerArrowRightIcon = muiGlyph(
  "mui-picker-arrow-right",
  PICKER_ARROW_RIGHT_PATH,
  "size-6",
);

// ## The table sort label's pair
//
// MRT's default sort icons (`index.esm.js:17,48,1264,1295`): `ArrowDownward`
// for a sorted column — one arrow, turned over for ascending — and `SyncAlt`
// for an unsorted one, which MRT stands upright with an inline transform
// (`:3483-3485`). The shared sort control (`mui-table-sort.tsx`) draws them at
// `TableSortLabel`'s 18px; they are declared at the 24px box like the rest, and
// the slot resizes them. Atlas drew lucide's `ArrowDown` / `ArrowUpDown` here
// until the control was lifted into the shared table (#740).

/** `@mui/icons-material/ArrowDownward.js` — also the Spec sort row's 20px
 *  direction toggle (`mui-spec-icons.tsx`). */
export const ARROW_DOWNWARD_PATH = "m20 12-1.41-1.41L13 16.17V4h-2v12.17l-5.58-5.59L4 12l8 8z";
/** `@mui/icons-material/SyncAlt.js`. */
const SYNC_ALT_PATH = "m18 12 4-4-4-4v3H3v2h15zM6 12l-4 4 4 4v-3h15v-2H6z";

export const MuiArrowDownwardIcon = muiGlyph("mui-arrow-downward", ARROW_DOWNWARD_PATH, "size-6");
export const MuiSyncAltIcon = muiGlyph("mui-sync-alt", SYNC_ALT_PATH, "size-6");
